import {
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { IconInfoCircle } from "@tabler/icons-react";
import type {
  FocusEvent,
  FocusEventHandler,
  KeyboardEvent,
  ReactNode,
} from "react";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type ShapeKey = "incisor" | "lateral" | "canine" | "premolar" | "molar";

type ToothShape = {
  x: number;
  y: number;
  width: number;
  height: number;
  rx: number;
  ry: number;
};

type ToothDatum = {
  id: number;
  tx: number;
  ty: number;
  rotate: number;
  shape: ShapeKey;
  lx: number;
  ly: number;
};

const TOOTH_SHAPES: Record<ShapeKey, ToothShape> = {
  incisor: { x: -9, y: -7.5, width: 18, height: 15, rx: 5, ry: 5 },
  lateral: { x: -7.5, y: -7, width: 15, height: 14, rx: 4.5, ry: 4.5 },
  canine: { x: -6, y: -9.5, width: 12, height: 19, rx: 5.5, ry: 5.5 },
  premolar: { x: -7, y: -8.5, width: 14, height: 17, rx: 5.5, ry: 5.5 },
  molar: { x: -10, y: -9, width: 20, height: 18, rx: 6.5, ry: 6.5 },
};

const TOOTH_RENDER_SCALE = 2;
const SOLIDARIZATION_HEIGHT_RATIO = 0.6;

const MAXILLARY_TEETH: ToothDatum[] = [
  // Quadrant 1 – upper right
  {
    id: 11,
    tx: 426.5518386558522,
    ty: 77.31319645100895,
    rotate: -11.2681699709062,
    shape: "incisor",
    lx: 417.1725741181931,
    ly: 30.23847503141252,
  },
  {
    id: 12,
    tx: 387.20934450993377,
    ty: 92.73889506206275,
    rotate: -31.550875918537315,
    shape: "lateral",
    lx: 362.09308231390725,
    ly: 51.83445308688786,
  },
  {
    id: 13,
    tx: 361.77288338097014,
    ty: 113.66135055773239,
    rotate: -47.326313877806,
    shape: "canine",
    lx: 326.48203673335814,
    ly: 81.12589078082534,
  },
  {
    id: 14,
    tx: 343.52164262585273,
    ty: 139.6614111952303,
    rotate: -62.53834333852936,
    shape: "premolar",
    lx: 300.93029967619384,
    ly: 117.52597567332242,
  },
  {
    id: 15,
    tx: 332.03214653844077,
    ty: 173.00942134294596,
    rotate: -79.44059829488864,
    shape: "premolar",
    lx: 284.84500515381706,
    ly: 164.21318988012433,
  },
  {
    id: 16,
    tx: 330,
    ty: 216.54444078461245,
    rotate: -90,
    shape: "molar",
    lx: 282,
    ly: 216.54444078461245,
  },
  {
    id: 17,
    tx: 330,
    ty: 268.46444078461246,
    rotate: -90,
    shape: "molar",
    lx: 282,
    ly: 268.46444078461246,
  },
  {
    id: 18,
    tx: 330,
    ty: 320.3844407846125,
    rotate: -90,
    shape: "molar",
    lx: 282,
    ly: 320.3844407846125,
  },
  // Quadrant 2 – upper left
  {
    id: 21,
    tx: 473.4481613441478,
    ty: 77.31319645100895,
    rotate: 11.2681699709062,
    shape: "incisor",
    lx: 482.82742588180696,
    ly: 30.23847503141252,
  },
  {
    id: 22,
    tx: 512.7906554900662,
    ty: 92.73889506206278,
    rotate: 31.55087591853733,
    shape: "lateral",
    lx: 537.9069176860928,
    ly: 51.83445308688789,
  },
  {
    id: 23,
    tx: 538.2271166190299,
    ty: 113.6613505577324,
    rotate: 47.326313877806,
    shape: "canine",
    lx: 573.5179632666418,
    ly: 81.12589078082536,
  },
  {
    id: 24,
    tx: 556.4783573741472,
    ty: 139.66141119523033,
    rotate: 62.53834333852936,
    shape: "premolar",
    lx: 599.0697003238062,
    ly: 117.52597567332248,
  },
  {
    id: 25,
    tx: 567.9678534615592,
    ty: 173.00942134294598,
    rotate: 79.44059829488866,
    shape: "premolar",
    lx: 615.154994846183,
    ly: 164.21318988012436,
  },
  {
    id: 26,
    tx: 570,
    ty: 216.54444078461245,
    rotate: 90,
    shape: "molar",
    lx: 618,
    ly: 216.54444078461245,
  },
  {
    id: 27,
    tx: 570,
    ty: 268.46444078461246,
    rotate: 90,
    shape: "molar",
    lx: 618,
    ly: 268.46444078461246,
  },
  {
    id: 28,
    tx: 570,
    ty: 320.3844407846125,
    rotate: 90,
    shape: "molar",
    lx: 618,
    ly: 320.3844407846125,
  },
];

const MANDIBULAR_TEETH: ToothDatum[] = [
  // Quadrant 3 – lower left
  {
    id: 31,
    tx: 473.4481613441478,
    ty: 652.6868035489911,
    rotate: 168.7318300290938,
    shape: "incisor",
    lx: 482.82742588180696,
    ly: 699.7615249685875,
  },
  {
    id: 32,
    tx: 512.7906554900662,
    ty: 637.2611049379373,
    rotate: 148.44912408146268,
    shape: "lateral",
    lx: 537.9069176860928,
    ly: 678.1655469131122,
  },
  {
    id: 33,
    tx: 538.2271166190299,
    ty: 616.3386494422676,
    rotate: 132.673686122194,
    shape: "canine",
    lx: 573.5179632666418,
    ly: 648.8741092191747,
  },
  {
    id: 34,
    tx: 556.4783573741472,
    ty: 590.3385888047696,
    rotate: 117.46165666147064,
    shape: "premolar",
    lx: 599.0697003238062,
    ly: 612.4740243266775,
  },
  {
    id: 35,
    tx: 567.9678534615592,
    ty: 556.990578657054,
    rotate: 100.55940170511134,
    shape: "premolar",
    lx: 615.154994846183,
    ly: 565.7868101198757,
  },
  {
    id: 36,
    tx: 570,
    ty: 513.4555592153876,
    rotate: 90,
    shape: "molar",
    lx: 618,
    ly: 513.4555592153876,
  },
  {
    id: 37,
    tx: 570,
    ty: 461.53555921538754,
    rotate: 90,
    shape: "molar",
    lx: 618,
    ly: 461.53555921538754,
  },
  {
    id: 38,
    tx: 570,
    ty: 409.6155592153875,
    rotate: 90,
    shape: "molar",
    lx: 618,
    ly: 409.6155592153875,
  },
  // Quadrant 4 – lower right
  {
    id: 41,
    tx: 426.5518386558522,
    ty: 652.6868035489911,
    rotate: 191.2681699709062,
    shape: "incisor",
    lx: 417.1725741181931,
    ly: 699.7615249685875,
  },
  {
    id: 42,
    tx: 387.20934450993377,
    ty: 637.2611049379373,
    rotate: 211.55087591853732,
    shape: "lateral",
    lx: 362.09308231390725,
    ly: 678.1655469131122,
  },
  {
    id: 43,
    tx: 361.77288338097014,
    ty: 616.3386494422676,
    rotate: 227.326313877806,
    shape: "canine",
    lx: 326.48203673335814,
    ly: 648.8741092191747,
  },
  {
    id: 44,
    tx: 343.52164262585273,
    ty: 590.3385888047696,
    rotate: 242.53834333852936,
    shape: "premolar",
    lx: 300.93029967619384,
    ly: 612.4740243266776,
  },
  {
    id: 45,
    tx: 332.03214653844077,
    ty: 556.990578657054,
    rotate: 259.4405982948887,
    shape: "premolar",
    lx: 284.84500515381706,
    ly: 565.7868101198757,
  },
  {
    id: 46,
    tx: 330,
    ty: 513.4555592153876,
    rotate: -90,
    shape: "molar",
    lx: 282,
    ly: 513.4555592153876,
  },
  {
    id: 47,
    tx: 330,
    ty: 461.53555921538754,
    rotate: -90,
    shape: "molar",
    lx: 282,
    ly: 461.53555921538754,
  },
  {
    id: 48,
    tx: 330,
    ty: 409.6155592153875,
    rotate: -90,
    shape: "molar",
    lx: 282,
    ly: 409.6155592153875,
  },
];

const TEETH: ToothDatum[] = [...MAXILLARY_TEETH, ...MANDIBULAR_TEETH];

const QUADRANTS: ToothDatum[][] = [
  MAXILLARY_TEETH.slice(0, 8),
  MAXILLARY_TEETH.slice(8, 16),
  MANDIBULAR_TEETH.slice(0, 8),
  MANDIBULAR_TEETH.slice(8, 16),
];

const ADJACENT_PAIRS: Array<[number, number]> = QUADRANTS.flatMap(quadrant =>
  quadrant
    .slice(0, -1)
    .map(
      (tooth, index) => [tooth.id, quadrant[index + 1].id] as [number, number]
    )
);

const CROSS_MIDLINE_PAIRS: Array<[number, number]> = [
  [11, 21],
  [31, 41],
];

const SOLIDARIZATION_LINKS: Array<[number, number]> = [
  ...ADJACENT_PAIRS,
  ...CROSS_MIDLINE_PAIRS,
];

const getPairKey = (firstToothId: number, secondToothId: number) => {
  const [minId, maxId] = [firstToothId, secondToothId].sort((a, b) => a - b);
  return `${minId}-${maxId}`;
};

const getSolidarizationPairKeys = (
  toothIds: Set<number>,
  disabledToothIds: Set<number>
) => {
  const pairKeys = new Set<string>();

  SOLIDARIZATION_LINKS.forEach(([firstToothId, secondToothId]) => {
    if (
      toothIds.has(firstToothId) &&
      toothIds.has(secondToothId) &&
      !disabledToothIds.has(firstToothId) &&
      !disabledToothIds.has(secondToothId)
    ) {
      pairKeys.add(getPairKey(firstToothId, secondToothId));
    }
  });

  return pairKeys;
};

const useStyles = createStyles(theme => {
  const selectedColor = theme.colors[theme.primaryColor][6];
  const strokedColor = theme.colors[theme.primaryColor][2];
  const errorColor = theme.colors.red[6];
  const errorStrokedColor = theme.colors.red[2];
  const disabledColor = theme.colors.gray[4];

  return {
    root: {
      flex: 1,
      minHeight: 0,
    },

    chartContainer: {
      flex: 1,
      minHeight: 0,
      width: "100%",
      justifyContent: "center",
      alignItems: "center",
    },

    chart: {
      display: "block",
      width: "100%",
      height: "100%",
      maxWidth: "42rem",
      minWidth: "min(20rem, 100%)",
      maxHeight: "100%",
    },

    label: {
      fontFamily: theme.fontFamily,
      fontSize: theme.fontSizes.sm,
      fill: theme.colors.gray[5],
      fontWeight: theme.fontWeights.bold,
    },

    labelSelected: {
      fill: selectedColor,
      stroke: selectedColor,
    },

    labelDisabled: {
      fill: disabledColor,
      stroke: disabledColor,
    },

    labelError: {
      fill: errorColor,
      stroke: errorColor,
    },

    tooth: {
      fill: "white",
      stroke: theme.colors.gray[5],
      cursor: "pointer",
      filter: "drop-shadow(0 1px 1px rgba(12,10,9,0.05))",
      outline: "none",
      "&:focus-visible": {
        outline: `2px solid ${selectedColor}`,
        outlineOffset: 2,
      },
    },

    toothFilled: {
      fill: selectedColor,
      stroke: selectedColor,
    },

    toothStroke: {
      fill: strokedColor,
      stroke: selectedColor,
      strokeDasharray: "3 2.5",
    },

    toothDisabled: {
      fill: disabledColor,
      stroke: disabledColor,
      cursor: "not-allowed",
    },
    toothFilledError: {
      fill: errorColor,
      stroke: errorColor,
    },
    toothStrokeError: {
      fill: errorStrokedColor,
      stroke: errorColor,
      strokeDasharray: "3 2.5",
    },

    solidarizationControl: {
      cursor: "pointer",
      outline: "none",
      "&:focus-visible rect": {
        outline: `2px solid ${selectedColor}`,
        outlineOffset: 2,
      },
    },

    solidarizationRect: {
      strokeWidth: 2,
      strokeDasharray: "4 3",
      vectorEffect: "non-scaling-stroke",
    },

    solidarizationRectEnabled: {
      stroke: selectedColor,
      fill: selectedColor,
      fillOpacity: 1,
    },

    solidarizationRectDisabled: {
      stroke: theme.colors.gray[6],
      fill: theme.colors.gray[2],
      fillOpacity: 0.2,
    },
  };
});

export type ToothObject = { tooth: number; value: string };
export type ToothFormat = number | ToothObject;

interface TeethSelectProps<T extends ToothFormat = number> {
  onClick: (toothId: number, newGroups: number[][]) => void;
  teeth: Array<T>;
  teethDisabled?: number[];
  teethError?: number[];
  error?: string;
  title?: string;
  withGrouping?: boolean;
  groupAdjacentByDefault?: boolean;
  groupedTeeth?: number[][];
  onGroupedTeethChange?: (groupedTeeth: number[][]) => void;
  onBlur?: FocusEventHandler<SVGGElement>;
  applyToothSelectionClass?: (
    tooth: ToothObject
  ) => "toothFilled" | "toothStroke";
  willUnselectTooth?: (toothId: number) => boolean;
  toothTooltip?: (toothId: number) => string;
  getTeethDescription?: (args: {
    teeth: T[];
    groupedTeeth?: number[][];
    descriptionLevel: "global" | "group";
  }) => {
    badgeContent: string;
    description: string;
  };
  additionalSection?: ReactNode;
  hideGroupAllButtons?: boolean;
  solidarizedLabel?: string;
}

const isSelectInteractiveElement = (
  element: EventTarget | null
): element is Element =>
  element instanceof Element &&
  element.hasAttribute("data-teeth-select-focusable");

const getToothId = <T extends ToothFormat>(value: T) => {
  return typeof value === "number" ? value : value.tooth;
};

export const TeethSelect = <T extends ToothFormat>({
  teeth,
  onClick,
  teethDisabled,
  teethError,
  error,
  withGrouping,
  groupAdjacentByDefault,
  groupedTeeth,
  onGroupedTeethChange,
  onBlur,
  applyToothSelectionClass,
  willUnselectTooth,
  toothTooltip,
  title,
  getTeethDescription,
  additionalSection,
  hideGroupAllButtons,
  solidarizedLabel,
}: TeethSelectProps<T>) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation("forms");

  const [
    disabledDefaultSolidarizationPairKeys,
    setDisabledDefaultSolidarizationPairKeys,
  ] = useState<Set<string>>(new Set());

  const teethById = useMemo(
    () => new Map(TEETH.map(tooth => [tooth.id, tooth])),
    []
  );

  const selectedToothIds = useMemo(
    () => new Set(teeth.map(getToothId)),
    [teeth]
  );
  const disabledToothIds = useMemo(
    () => new Set(teethDisabled ?? []),
    [teethDisabled]
  );

  const selectedSolidarizationPairKeys = useMemo(
    () => getSolidarizationPairKeys(selectedToothIds, disabledToothIds),
    [selectedToothIds, disabledToothIds]
  );

  const selectedSolidarizationLinks = useMemo(
    () =>
      SOLIDARIZATION_LINKS.filter(([firstToothId, secondToothId]) =>
        selectedSolidarizationPairKeys.has(
          getPairKey(firstToothId, secondToothId)
        )
      ),
    [selectedSolidarizationPairKeys]
  );

  const enabledSolidarizationPairKeys = useMemo(() => {
    const keys = new Set<string>();

    groupedTeeth?.forEach(group => {
      const groupTeethSet = new Set(group.map(getToothId));

      SOLIDARIZATION_LINKS.forEach(([firstToothId, secondToothId]) => {
        if (
          groupTeethSet.has(firstToothId) &&
          groupTeethSet.has(secondToothId)
        ) {
          keys.add(getPairKey(firstToothId, secondToothId));
        }
      });
    });

    return keys;
  }, [groupedTeeth]);

  const resolvedEnabledSolidarizationPairKeys = useMemo(() => {
    const keys = new Set(enabledSolidarizationPairKeys);

    if (groupAdjacentByDefault) {
      selectedSolidarizationPairKeys.forEach(pairKey => {
        keys.add(pairKey);
      });

      disabledDefaultSolidarizationPairKeys.forEach(pairKey => {
        keys.delete(pairKey);
      });
    }

    return keys;
  }, [
    enabledSolidarizationPairKeys,
    selectedSolidarizationPairKeys,
    groupAdjacentByDefault,
    disabledDefaultSolidarizationPairKeys,
  ]);

  const selectedSolidarizationGroups = useMemo(() => {
    const adjacency = new Map<number, Set<number>>();

    selectedSolidarizationLinks.forEach(([firstToothId, secondToothId]) => {
      if (!adjacency.has(firstToothId)) {
        adjacency.set(firstToothId, new Set());
      }
      if (!adjacency.has(secondToothId)) {
        adjacency.set(secondToothId, new Set());
      }

      adjacency.get(firstToothId)?.add(secondToothId);
      adjacency.get(secondToothId)?.add(firstToothId);
    });

    const visited = new Set<number>();
    const groups: number[][] = [];

    adjacency.forEach((_, startToothId) => {
      if (visited.has(startToothId)) {
        return;
      }

      const stack = [startToothId];
      const group: number[] = [];

      while (stack.length > 0) {
        const currentToothId = stack.pop();

        if (currentToothId === undefined || visited.has(currentToothId)) {
          continue;
        }

        visited.add(currentToothId);
        group.push(currentToothId);

        adjacency.get(currentToothId)?.forEach(nextToothId => {
          if (!visited.has(nextToothId)) {
            stack.push(nextToothId);
          }
        });
      }

      if (group.length > 1) {
        groups.push(group.sort((a, b) => a - b));
      }
    });

    return groups;
  }, [selectedSolidarizationLinks]);

  const buildGroupsFromPairKeys = (
    pairKeys: Set<string>,
    selectedToothIdsSet: Set<number>
  ) => {
    const adjacency = new Map<number, Set<number>>();

    SOLIDARIZATION_LINKS.forEach(([firstToothId, secondToothId]) => {
      const pairKey = getPairKey(firstToothId, secondToothId);

      if (
        !pairKeys.has(pairKey) ||
        !selectedToothIdsSet.has(firstToothId) ||
        !selectedToothIdsSet.has(secondToothId)
      ) {
        return;
      }

      if (!adjacency.has(firstToothId)) {
        adjacency.set(firstToothId, new Set());
      }
      if (!adjacency.has(secondToothId)) {
        adjacency.set(secondToothId, new Set());
      }

      adjacency.get(firstToothId)?.add(secondToothId);
      adjacency.get(secondToothId)?.add(firstToothId);
    });

    const visited = new Set<number>();
    const groups: number[][] = [];
    const sortedSelectedToothIds = [...selectedToothIdsSet].sort(
      (a, b) => a - b
    );

    sortedSelectedToothIds.forEach(startToothId => {
      if (visited.has(startToothId)) {
        return;
      }

      const stack = [startToothId];
      const group: number[] = [];

      while (stack.length > 0) {
        const currentToothId = stack.pop();

        if (currentToothId === undefined || visited.has(currentToothId)) {
          continue;
        }

        visited.add(currentToothId);
        group.push(currentToothId);

        adjacency.get(currentToothId)?.forEach(nextToothId => {
          if (!visited.has(nextToothId)) {
            stack.push(nextToothId);
          }
        });
      }

      if (group.length > 1) {
        groups.push(group.sort((a, b) => a - b));
      } else if (group.length === 1) {
        groups.push(group);
      }
    });

    return groups;
  };

  const activateTooth = (toothId: number) => {
    if (teethDisabled?.includes(toothId)) {
      return;
    }

    if (withGrouping) {
      const nextSelectedToothIds = new Set(selectedToothIds);

      if (
        nextSelectedToothIds.has(toothId) &&
        (!willUnselectTooth || willUnselectTooth(toothId))
      ) {
        nextSelectedToothIds.delete(toothId);
      } else {
        nextSelectedToothIds.add(toothId);
      }

      const nextResolvedEnabledPairKeys = new Set(
        enabledSolidarizationPairKeys
      );

      if (groupAdjacentByDefault) {
        const nextSelectedSolidarizationPairKeys = getSolidarizationPairKeys(
          nextSelectedToothIds,
          disabledToothIds
        );

        nextSelectedSolidarizationPairKeys.forEach(pairKey => {
          nextResolvedEnabledPairKeys.add(pairKey);
        });

        disabledDefaultSolidarizationPairKeys.forEach(pairKey => {
          nextResolvedEnabledPairKeys.delete(pairKey);
        });
      }

      const newGroups = buildGroupsFromPairKeys(
        nextResolvedEnabledPairKeys,
        nextSelectedToothIds
      );

      onGroupedTeethChange?.(newGroups);
      onClick(toothId, newGroups);
    } else {
      onClick(toothId, []);
    }
  };

  const handleToothKeyDown = (
    event: KeyboardEvent<SVGGElement>,
    toothId: number
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      activateTooth(toothId);
    }
  };

  const handleSelectBlur = (event: FocusEvent<SVGGElement>) => {
    if (isSelectInteractiveElement(event.relatedTarget)) {
      return;
    }

    onBlur?.(event);
  };

  const getToothClassName = (toothId: number) => {
    const tooth = teeth.find(tooth =>
      typeof tooth === "number" ? tooth === toothId : tooth.tooth === toothId
    );
    const isSelected = !!tooth;
    const isDisabled = teethDisabled?.includes(toothId);
    const isError = teethError?.includes(toothId);

    if (isDisabled) {
      return classes.toothDisabled;
    }

    if (isError) {
      if (applyToothSelectionClass && tooth && typeof tooth !== "number") {
        return classes[`${applyToothSelectionClass(tooth)}Error`];
      }
      return classes.toothFilledError;
    }

    if (isSelected) {
      if (applyToothSelectionClass && typeof tooth !== "number") {
        return classes[applyToothSelectionClass(tooth)];
      }
      return classes.toothFilled;
    }
  };

  const getLabelClassName = (toothId: number) => {
    const isSelected = teeth.some(tooth =>
      typeof tooth === "number" ? tooth === toothId : tooth.tooth === toothId
    );
    const isDisabled = teethDisabled?.includes(toothId);
    const isError = teethError?.includes(toothId);

    if (isDisabled) {
      return classes.labelDisabled;
    }

    if (isError) {
      return classes.labelError;
    }

    if (isSelected) {
      return classes.labelSelected;
    }
  };

  const renderTooth = (tooth: ToothDatum) => {
    const s = TOOTH_SHAPES[tooth.shape];

    const isSelected = teeth.some(t =>
      typeof t === "number" ? t === tooth.id : t.tooth === tooth.id
    );
    const isDisabled = teethDisabled?.includes(tooth.id);

    return (
      <Tooltip
        disabled={!toothTooltip}
        label={toothTooltip?.(tooth.id)}
        position="top"
        withArrow
        key={tooth.id}
      >
        <g
          key={tooth.id}
          role="button"
          data-teeth-select-focusable
          onBlur={handleSelectBlur}
          tabIndex={isDisabled ? -1 : 0}
          // eslint-disable-next-line @eslint-react/dom-no-unknown-property -- legacy IE/Edge attribute, kept on purpose
          focusable={isDisabled ? "false" : "true"}
          aria-pressed={isDisabled ? false : isSelected}
          aria-disabled={isDisabled}
          aria-label={`${t("components.teethSelect.labels.tooth")} ${tooth.id}`}
          className={classes.tooth}
          onClick={() => activateTooth(tooth.id)}
          onKeyDown={event => handleToothKeyDown(event, tooth.id)}
        >
          <rect
            x={s.x}
            y={s.y}
            width={s.width}
            height={s.height}
            rx={s.rx}
            ry={s.ry}
            transform={`translate(${tooth.tx} ${tooth.ty}) rotate(${tooth.rotate}) scale(${TOOTH_RENDER_SCALE})`}
            vectorEffect="non-scaling-stroke"
            className={getToothClassName(tooth.id)}
          />
        </g>
      </Tooltip>
    );
  };

  const toggleSolidarization = (
    firstToothId: number,
    secondToothId: number
  ) => {
    const pairKey = getPairKey(firstToothId, secondToothId);
    const isCurrentlySolidarized =
      resolvedEnabledSolidarizationPairKeys.has(pairKey);
    const nextEnabledPairKeys = new Set(enabledSolidarizationPairKeys);
    const nextDisabledDefaultPairKeys = new Set(
      disabledDefaultSolidarizationPairKeys
    );

    if (isCurrentlySolidarized) {
      nextEnabledPairKeys.delete(pairKey);

      if (
        groupAdjacentByDefault &&
        selectedSolidarizationPairKeys.has(pairKey)
      ) {
        nextDisabledDefaultPairKeys.add(pairKey);
      }
    } else {
      nextDisabledDefaultPairKeys.delete(pairKey);
      nextEnabledPairKeys.add(pairKey);
    }

    setDisabledDefaultSolidarizationPairKeys(nextDisabledDefaultPairKeys);

    onGroupedTeethChange?.(
      buildGroupsFromPairKeys(nextEnabledPairKeys, selectedToothIds)
    );
  };

  const handleSolidarizationKeyDown = (
    event: KeyboardEvent<SVGGElement>,
    firstToothId: number,
    secondToothId: number
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleSolidarization(firstToothId, secondToothId);
    }
  };

  const renderSolidarizationToggle = (
    firstToothId: number,
    secondToothId: number
  ) => {
    const firstTooth = teethById.get(firstToothId);
    const secondTooth = teethById.get(secondToothId);

    if (!firstTooth || !secondTooth) {
      return null;
    }

    const pairKey = getPairKey(firstToothId, secondToothId);
    const isSolidarized = resolvedEnabledSolidarizationPairKeys.has(pairKey);

    const dx = secondTooth.tx - firstTooth.tx;
    const dy = secondTooth.ty - firstTooth.ty;
    const distance = Math.hypot(dx, dy);
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
    const centerX = (firstTooth.tx + secondTooth.tx) / 2;
    const centerY = (firstTooth.ty + secondTooth.ty) / 2;

    const width = Math.max(distance - 30, 10);
    const group = selectedSolidarizationGroups.find(
      teethGroup =>
        teethGroup.includes(firstToothId) && teethGroup.includes(secondToothId)
    ) ?? [firstToothId, secondToothId];

    const smallestGroupToothRenderedHeight = Math.min(
      ...group.map(toothId => {
        const tooth = teethById.get(toothId);

        if (!tooth) {
          return Number.POSITIVE_INFINITY;
        }

        return TOOTH_SHAPES[tooth.shape].height * TOOTH_RENDER_SCALE;
      })
    );

    const height =
      smallestGroupToothRenderedHeight * SOLIDARIZATION_HEIGHT_RATIO;

    return (
      <Tooltip
        label={t("components.teethSelect.tooltips.groupButton")}
        position="top"
        withArrow
        key={pairKey}
      >
        <g
          role="button"
          data-teeth-select-focusable
          onBlur={handleSelectBlur}
          tabIndex={0}
          // eslint-disable-next-line @eslint-react/dom-no-unknown-property -- legacy IE/Edge attribute, kept on purpose
          focusable="true"
          aria-pressed={isSolidarized}
          aria-label={t("components.teethSelect.labels.groupButton", {
            firstTooth: firstToothId,
            secondTooth: secondToothId,
          })}
          className={classes.solidarizationControl}
          onClick={() => toggleSolidarization(firstToothId, secondToothId)}
          onKeyDown={event =>
            handleSolidarizationKeyDown(event, firstToothId, secondToothId)
          }
        >
          <rect
            x={-width / 2}
            y={-height / 2}
            width={width}
            height={height}
            rx={3}
            ry={3}
            transform={`translate(${centerX} ${centerY}) rotate(${angle})`}
            className={cx(
              classes.solidarizationRect,
              isSolidarized
                ? classes.solidarizationRectEnabled
                : classes.solidarizationRectDisabled
            )}
          />
        </g>
      </Tooltip>
    );
  };

  const teethDescription = getTeethDescription?.({
    teeth,
    groupedTeeth,
    descriptionLevel: "global",
  });
  const solidarizedGroups =
    groupedTeeth?.filter(group => group.length > 1) ?? [];

  return (
    <Card>
      <Stack h="100%" className={classes.root} gap="s5">
        <Group justify="space-between">
          <Text size="sm" fw={600}>
            {title ?? t("components.teethSelect.title")}
          </Text>
          <Group gap="s1" c="ink.3">
            <IconInfoCircle size={16} />
            <Text variant="caption">
              {t("components.teethSelect.legends.toothNumbering")}
            </Text>
          </Group>
        </Group>
        <Flex className={classes.chartContainer}>
          <svg
            className={classes.chart}
            data-select="single"
            viewBox="0 0 900 720"
            xmlns="http://www.w3.org/2000/svg"
          >
            <text className={classes.label} x="450" y="240" textAnchor="middle">
              {t("components.teethSelect.labels.maxillary")}
            </text>
            <text className={classes.label} x="450" y="508" textAnchor="middle">
              {t("components.teethSelect.labels.mandibular")}
            </text>
            <text className={classes.label} x="248" y="352" textAnchor="end">
              {t("components.teethSelect.labels.right")}
            </text>
            <text className={classes.label} x="652" y="352" textAnchor="start">
              {t("components.teethSelect.labels.left")}
            </text>
            <g className="teeth-layer">
              {MAXILLARY_TEETH.map(tooth => renderTooth(tooth))}
              {MANDIBULAR_TEETH.map(tooth => renderTooth(tooth))}
            </g>
            {withGrouping && (
              <g className="solidarization-layer">
                {selectedSolidarizationLinks.map(
                  ([firstToothId, secondToothId]) =>
                    renderSolidarizationToggle(firstToothId, secondToothId)
                )}
              </g>
            )}
            <g className="labels-layer">
              {TEETH.map(tooth => (
                <text
                  key={tooth.id}
                  x={tooth.lx}
                  y={tooth.ly}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className={getLabelClassName(tooth.id)}
                >
                  {tooth.id}
                </text>
              ))}
            </g>
          </svg>
        </Flex>
        <Divider />
        {teethDescription && (
          <Group align="center" gap="s2">
            <Badge
              leftSection={null}
              color="brand"
              variant="filled"
              radius="sm"
              size="lg"
            >
              {teethDescription.badgeContent}
            </Badge>
            <Text size="sm" fw={500} c="ink.9">
              {teethDescription.description}
            </Text>
          </Group>
        )}
        {withGrouping && (
          <Group>
            <Group gap="s1">
              <Paper
                w={20}
                h={14}
                bd={`dashed 1px var(--mantine-color-ink-5)`}
                radius="xs"
              />
              <Text variant="caption">
                {t("components.teethSelect.legends.splittedElements")}
              </Text>
            </Group>

            <Group gap="s1">
              <Paper w={20} h={14} bg="brand" radius="xs" />
              <Text variant="caption">
                {t("components.teethSelect.legends.solidarizedElements")}
              </Text>
            </Group>

            <Text variant="caption">
              {t("components.teethSelect.legends.solidarizationHelp")}
            </Text>
          </Group>
        )}
        {additionalSection && (
          <>
            <Divider />
            {additionalSection}
          </>
        )}
        {withGrouping && (
          <>
            <Divider />
            <Group justify="space-between" wrap="nowrap" align="center">
              <Box flex={1} miw={0}>
                {teeth.length > 1 &&
                  !!getTeethDescription &&
                  (!solidarizedGroups.length ? (
                    <Text variant="caption" size="sm">
                      {t("components.teethSelect.legends.noSolidarization", {
                        count: selectedSolidarizationLinks.length,
                      })}
                    </Text>
                  ) : (
                    <Group gap="s1">
                      <Text variant="caption" size="sm">
                        {solidarizedLabel ??
                          t("components.teethSelect.legends.solidarized", {
                            count: solidarizedGroups.length,
                          })}{" "}
                        :
                      </Text>
                      <Group gap="s1">
                        {solidarizedGroups.map((group, index) => {
                          const groupDescription = getTeethDescription({
                            teeth: teeth.filter(tooth =>
                              group.includes(getToothId(tooth))
                            ),
                            groupedTeeth: [group],
                            descriptionLevel: "group",
                          });
                          return (
                            <Fragment key={group.join("-")}>
                              <Text variant="caption" size="sm" fw={500}>
                                {groupDescription.badgeContent} (
                                {groupDescription.description})
                              </Text>
                              {index < solidarizedGroups.length - 1 && (
                                <Text variant="caption" size="sm">
                                  ·
                                </Text>
                              )}
                            </Fragment>
                          );
                        })}
                      </Group>
                    </Group>
                  ))}
              </Box>
              {!hideGroupAllButtons && (
                <Group justify="center" wrap="nowrap" style={{ flexShrink: 0 }}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDisabledDefaultSolidarizationPairKeys(new Set());

                      onGroupedTeethChange?.(
                        buildGroupsFromPairKeys(
                          new Set(
                            selectedSolidarizationLinks.map(
                              ([firstToothId, secondToothId]) =>
                                getPairKey(firstToothId, secondToothId)
                            )
                          ),
                          selectedToothIds
                        )
                      );
                    }}
                  >
                    {t("components.teethSelect.buttons.groupAll")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (groupAdjacentByDefault) {
                        setDisabledDefaultSolidarizationPairKeys(
                          new Set(selectedSolidarizationPairKeys)
                        );
                      } else {
                        setDisabledDefaultSolidarizationPairKeys(new Set());
                      }

                      onGroupedTeethChange?.(
                        buildGroupsFromPairKeys(new Set(), selectedToothIds)
                      );
                    }}
                  >
                    {t("components.teethSelect.buttons.dissociateAll")}
                  </Button>
                </Group>
              )}
            </Group>
          </>
        )}
        {error && (
          <Flex justify="center">
            <Text size="sm" c="red" fw="bold">
              {error}
            </Text>
          </Flex>
        )}
      </Stack>
    </Card>
  );
};
